import { Dispatch, SetStateAction, useState } from "react";
import type { DailyPuzzle } from "@/lib/types/game";
import type { LogicRule } from "@/lib/game-utils";
import type { GameResult } from "@/hooks/useGameFlow";
import { useGameResultView } from "@/hooks/useGameResultView";
import { GameResultView } from "./GameResultView";
import { GameController } from "./GameController";

interface GamePlayResultViewProps {
  todaysPuzzle: DailyPuzzle | null;
  userEmail: string | null;
  viewingPreviousResults: boolean;
  hasCompletedToday: boolean;
  filteredRules: LogicRule[];
  setFilteredRules: Dispatch<SetStateAction<LogicRule[]>>;
  setViewingPreviousResults: Dispatch<SetStateAction<boolean>>;
  isUsingExternalData?: boolean;
  onGameStart: (email: string) => void;
  isLoading?: boolean;
  gameResult?: GameResult;
  onClearCompletedStatus?: () => void;
}

export function GamePlayResultView({
  todaysPuzzle,
  userEmail,
  viewingPreviousResults,
  hasCompletedToday,
  filteredRules,
  setFilteredRules,
  setViewingPreviousResults,
  isUsingExternalData = false,
  onGameStart,
  isLoading = false,
  gameResult,
  onClearCompletedStatus
}: GamePlayResultViewProps) {
  // Use the custom hook to manage the game result view state
  const {
    showGamePlay,
    setShowGamePlay,
    currentGameResult,
    setCurrentGameResult,
    internalCompletedToday,
    todayActual
  } = useGameResultView({
    todaysPuzzle,
    viewingPreviousResults,
    setViewingPreviousResults,
    hasCompletedToday,
    gameResult
  });
  
  // If viewing previous results or has completed today, show the result container
  if (viewingPreviousResults || internalCompletedToday) {
    return (
      <GameResultView 
        gameResult={gameResult}
        currentGameResult={currentGameResult}
        todaysPuzzle={todaysPuzzle}
        userEmail={userEmail}
        filteredRules={filteredRules}
        isUsingExternalData={isUsingExternalData}
      />
    );
  }
  
  // Otherwise, show the game controller component
  return (
    <GameController
      userEmail={userEmail}
      todaysPuzzle={todaysPuzzle}
      showGamePlay={showGamePlay}
      setShowGamePlay={setShowGamePlay}
      setViewingPreviousResults={setViewingPreviousResults}
      setCurrentGameResult={setCurrentGameResult}
      onGameStart={onGameStart}
      setFilteredRules={setFilteredRules}
      filteredRules={filteredRules}
      hasCompletedToday={internalCompletedToday}
      isLoading={isLoading}
      todayActual={todayActual}
      onClearCompletedStatus={onClearCompletedStatus}
    />
  );
}
